import { Attach } from '@/components';
import { useAppClippo } from '@/core';
import { useClippoBlobs } from '@/utils';
import { Row, Typography } from 'antd';
import {
  useGetPersonalizationItemsQuery,
  useGetSpecialRequestsQuery,
} from '@/redux';
import { Trans, useTranslation } from 'react-i18next';
import styles from './HomePersonalizationAttachmentsPanel.module.scss';

interface Props {
  homeId: string;
  projectId: string;
  className?: string;
}

const { Text } = Typography;

export const HomePersonalizationAttachmentsPanel = (props: Props) => {
  const { homeId, projectId, className } = props;
  const { data: personalizationItems } = useGetPersonalizationItemsQuery({
    homeId,
    projectId,
  });
  const { data: specialRequests } = useGetSpecialRequestsQuery({
    homeId,
    projectId,
  });

  const { t } = useTranslation();

  if (!personalizationItems?.length && !specialRequests?.length) {
    return null;
  }

  return (
    <div className={className}>
      <Row>
        <Text className={styles.color}>
          <Trans
            i18nKey={t('personalization.attachments.title')}
            components={{ strong: <strong />, italic: <em /> }}
          />
        </Text>
      </Row>

      {personalizationItems?.map((item) => (
        <ItemAttachment
          key={item.id}
          itemId={item.id}
          itemName={item.name}
          number={item.number}
        />
      ))}
      {specialRequests?.map((item) => (
        <SpecialRequestFiles
          key={item.id}
          itemId={item.id}
          itemName={item.name}
          number={item.number}
        />
      ))}
    </div>
  );
};

interface ItemProps {
  itemId: string;
  itemName: string;
  number?: number;
}

function SpecialRequestFiles(props: ItemProps) {
  const { itemId, itemName, number } = props;
  const attachmentProps = useAppClippo({
    defer: false,
    ownerId: itemId,
    ownerType: 'SpecialRequestAttachments',
  });
  const imageProps = useAppClippo({
    defer: false,
    ownerId: itemId,
    ownerType: 'SpecialRequestImage',
  });
  const attachmentBlobs = useClippoBlobs(attachmentProps);
  const imageBlobs = useClippoBlobs(imageProps);

  if (!attachmentBlobs.blobs?.length && !imageBlobs.blobs?.length) {
    return null;
  }

  return (
    <div className="mt-3">
      <Text className={styles.color} strong italic>
        {itemName}
        {number != null && ` (${number})`}:
      </Text>

      {!!attachmentBlobs.blobs?.length && (
        <Attach
          {...attachmentProps}
          showAdd={false}
          multiple
          blobs={attachmentBlobs.blobs}
          showActions
          openOnClick={false}
        />
      )}
      {!!imageBlobs.blobs?.length && (
        <Attach
          {...imageProps}
          showAdd={false}
          multiple
          blobs={imageBlobs.blobs}
          showActions
          openOnClick={false}
        />
      )}
    </div>
  );
}

function ItemAttachment(props: ItemProps) {
  const { itemId, itemName, number } = props;

  const clippoProps = useAppClippo({
    defer: false,
    ownerId: itemId,
    ownerType: 'PersonalizationAttachments',
  });

  const clippoBlobs = useClippoBlobs(clippoProps);

  if (!clippoBlobs.blobs?.length) {
    return null;
  }

  return (
    <div className="mt-3">
      <Text className={styles.color} strong italic>
        {itemName}
        {number != null && ` (${number})`}:
      </Text>

      <Attach
        {...clippoProps}
        showAdd={false}
        multiple
        blobs={clippoBlobs.blobs}
        showActions
        openOnClick={false}
      />
    </div>
  );
}
